import {useEffect, useState} from 'react';
import {AuthService} from "../../../services/AuthService";
import {EnvService} from "../../../services/EnvService";

/**
 * Fetches the logo for the given insurance-provider ID from the backend,
 * returning a short-lived blob URL suitable for use in an <img> src.
 *
 * Pass `hasLogo={false}` to skip the network request entirely and go straight
 * to the fallback — eliminating 404s for providers that have no uploaded logo.
 *
 * The blob URL is automatically revoked when the hook unmounts or the id changes.
 */
export function useLogoBlob(id: number | undefined, hasLogo = true): { blobUrl: string | null; failed: boolean } {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!id || !hasLogo) {
            setBlobUrl(null);
            setFailed(true);
            return;
        }

        setBlobUrl(null);
        setFailed(false);

        let objectUrl: string | null = null;
        const token = AuthService.getUserTokens();
        const backendUrl = EnvService.getBackEndUrl() ?? '';

        fetch(`${backendUrl}/insuranceProviders/${id}/logo`, {
            headers: token ? {Authorization: `Bearer ${token}`} : {}
        })
            .then(res => {
                if (!res.ok) throw new Error('Not found');
                return res.blob();
            })
            .then(blob => {
                objectUrl = URL.createObjectURL(blob);
                setBlobUrl(objectUrl);
            })
            .catch(() => setFailed(true));

        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [id, hasLogo]);

    return {blobUrl, failed};
}
